<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed, nextTick } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import { usePipelineStore } from "@/stores/pipelineStore";
import PipelineTable from "./pipeline-table.vue";
import PipelineModal from "@/components/modal/pipeline-modal.vue";
import Modal from "bootstrap/js/dist/modal";

const title = "Pipeline";
const text = "CRM";
const text1 = "Pipeline List";

const editItem = ref(null);
const store = usePipelineStore();

const dateRangeInput = ref(null);
const stageFilter = ref(""); // UI dropdown placeholder (not sent unless you want)
const sortLabel = ref("Last 7 Days"); // placeholder label

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function booking_range(start, end) {
  const s = start.format("YYYY-MM-DD");
  const e = end.format("YYYY-MM-DD");
  store.filters.date_from = s;
  store.filters.date_to = e;
}

// function openEdit(row) {
//   const clone =
//     typeof structuredClone === "function"
//       ? structuredClone(row)
//       : JSON.parse(JSON.stringify(row));

//   editItem.value = clone;
//   nextTick(() => {
//     const el = document.getElementById("edit_pipeline");
//     Modal.getOrCreateInstance(el)?.show();
//   });
// }

async function openEdit(row) {
  const full = await store.fetchOne(row.id); // guarantees stages are present/normalized
  editItem.value = full;
  await nextTick();
  const el = document.getElementById("edit_pipeline");
  Modal.getOrCreateInstance(el)?.show();
}

async function openDelete(row) {
  const full = await store.fetchOne(row.id); // guarantees stages are present/normalized
  editItem.value = full;
  await nextTick();
  const el = document.getElementById("delete_pipeline");
  Modal.getOrCreateInstance(el)?.show();
}

function openAdd() {
  editItem.value = null;
  nextTick(() => {
    const el = document.getElementById("add_pipeline");
    Modal.getOrCreateInstance(el)?.show();
  });
}

function onSaved() {
  closeAllModals();
  editItem.value = null;
  store.fetchAll({ page: 1 });
}

onMounted(() => {
  const start = moment().subtract(6, "days");
  const end = moment();
  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range
    );
    booking_range(start, end);
  }

  const ids = [
    "add_pipeline",
    "edit_pipeline",
    "add_stage",
    "edit_stage",
    "delete_pipeline",
  ];
  ids.forEach((id) => {
    const el = document.getElementById(id);
    el?.addEventListener("hidden.bs.modal", cleanupBackdrops);
  });

  document.addEventListener("show.bs.modal", cleanupBackdrops);

  store.fetchAll();
});

const totalPages = computed(() => {
  const t = store.pagination.total || 0;
  const p = store.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

function cleanupBackdrops() {
  // remove any stray backdrops + body flags
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("padding-right");
  document.body.style.removeProperty("overflow");
}

function hideById(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const inst = Modal.getInstance(el) || Modal.getOrCreateInstance(el);
  inst?.hide();
}

function closeAllModals() {
  ["add_pipeline", "edit_pipeline", "add_stage", "edit_stage"].forEach(
    hideById
  );
  // give Bootstrap a tick to dispatch hidden events, then hard-clean
  setTimeout(cleanupBackdrops, 50);
}

function exportCsv() {
  const header = ["Pipeline", "Status", "Stages", "Created At"];
  const body = (store.items || []).map((pipeline) => [
    pipeline.name || "",
    pipeline.status || "",
    Array.isArray(pipeline.stages)
      ? pipeline.stages.map((stage) => stage.name).join(" | ")
      : "",
    pipeline.created_at || "",
  ]);
  const csv = [header, ...body]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `crm-pipelines-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportCsv"
                    v-can="'Pipeline.Export'"
                    ><i class="ti ti-file-type-csv me-1"></i>Export as CSV</a
                  >
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-2">
            <!-- replace the anchor -->
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAdd"
              v-can="'Pipeline.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Add New Pipeline
            </a>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Filters row in card header -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Pipeline List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon"
                  ><i class="ti ti-chevron-down"></i
                ></span>
              </div>
            </div>

            <div class="me-3">
              <input
                v-model.trim="store.filters.q"
                class="form-control"
                placeholder="Search pipeline name"
                @keyup.enter="store.fetchAll({ page: 1 })"
              />
            </div>

            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ store.filters.status || "Select Status" }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.status = '';
                      store.fetchAll({ page: 1 });
                    "
                    >All</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.status = 'Active';
                      store.fetchAll({ page: 1 });
                    "
                    >Active</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      store.filters.status = 'Inactive';
                      store.fetchAll({ page: 1 });
                    "
                    >Inactive</a
                  >
                </li>
              </ul>
            </div>

            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="
                      sortLabel = 'Recently Added';
                      store.fetchAll({ page: 1 });
                    "
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    href="javascript:void(0);"
                    @click="sortLabel = 'Last 7 Days'"
                    >Last 7 Days</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <pipeline-table
              :rows="store.items"
              :loading="store.loading"
              @edit="openEdit"
              @delete="openDelete"
            />
          </div>

          <div class="p-3 d-flex justify-content-between align-items-center">
            <div>
              Showing page {{ store.pagination.current_page }} of
              {{ totalPages }}
            </div>
            <div class="btn-group">
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page <= 1"
                @click="
                  store.fetchAll({ page: store.pagination.current_page - 1 })
                "
              >
                Prev
              </button>
              <button
                class="btn btn-outline-secondary"
                :disabled="store.pagination.current_page >= totalPages"
                @click="
                  store.fetchAll({ page: store.pagination.current_page + 1 })
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <pipeline-modal :edit-item="editItem" @saved="onSaved" />
</template>


